import { useParams, Link, useLocation } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Star, Clock, Users, Coffee, MapPin, MonitorPlay } from 'lucide-react';
import { useState, useEffect } from 'react';
import {
  type CancellationPolicy,
  getCancellationPolicyStatement,
} from '@shared/cancellation-policy';
import { DELIVERY_MODE_LABELS, isDeliveryMode } from '@shared/delivery-mode';
import { getRegistrationCutoffStatement } from '@shared/registration-cutoff';
import { isDemoClassTitle } from '@shared/demo-classes';
import { effectiveUnitPrice, isPromoActiveForSession } from '@shared/promo-pricing';
import type {
  ClassDetailDto,
  ClassSessionDto,
  CreatorClassSummaryDto,
} from '@shared/api-contracts';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError, redirectToLogin } from '@/lib/authUtils';
import { apiRequest, queryClient, fetchJson } from '@/lib/queryClient';
import { authedJson } from '@/lib/http';
import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { formatSessionCompact } from '@/lib/dateUtils';
import { normalizeImageUrl, imageErrorFallback, PLACEHOLDER } from '@/lib/image';
import { trackPublicFunnelEvent } from '@/lib/public-analytics';
import { useGeographyContext } from '@/hooks/useGeographyContext';
import { AutoRedirect } from '@/components/auto-redirect';
import { ClassWaitlistForm } from '@/components/class-waitlist-form';

type CheckoutSessionCreateResponse = {
  id: string;
};
type CheckoutLaunchPayload = {
  checkoutSessionId: string;
  classId: string;
  sessionId: string;
  quantity: number;
};
type CreatorClassesResponse = {
  classes?: CreatorClassSummaryDto[];
};

export default function ClassDetail() {
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const { locality: activeLocality, region: activeRegion } = useGeographyContext();
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewBody, setReviewBody] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const returnToParam = new window.URLSearchParams(window.location.search).get('returnTo');
  const returnHref = returnToParam ? decodeURIComponent(returnToParam) : '/classes';

  // Class details are publicly accessible

  const {
    data: classData,
    isLoading,
    error,
  } = useQuery<ClassDetailDto>({
    queryKey: ['/api/classes', id],
  });

  useEffect(() => {
    setActiveImage(null);
  }, [id]);

  const { data: enrollmentData } = useQuery<{
    enrolled: boolean;
    enrollment: { id: string; quantity: number; orderId: string | null } | null;
  }>({
    queryKey: ['/api/enrollments/sessions', selectedSessionId],
    queryFn: async () => {
      if (!selectedSessionId) {
        return { enrolled: false, enrollment: null };
      }

      return authedJson(`/api/enrollments/sessions/${selectedSessionId}`);
    },
    enabled: isAuthenticated && !!selectedSessionId,
  });

  const { data: reviewsData } = useQuery<{
    count: number;
    averageRating: number | null;
    reviews: Array<{
      id: string;
      rating: number;
      body: string | null;
      createdAt: string;
      user: { firstName: string | null; lastName: string | null };
    }>;
  }>({
    queryKey: ['/api/classes', id, 'reviews'],
    queryFn: () =>
      fetchJson<{
        count: number;
        averageRating: number | null;
        reviews: Array<{
          id: string;
          rating: number;
          body: string | null;
          createdAt: string;
          user: { firstName: string | null; lastName: string | null };
        }>;
      }>(`/api/classes/${id}/reviews`),
    enabled: !!id,
  });

  const { data: myPassData } = useQuery<{
    pass: {
      id: string;
      totalSessions: number;
      usedSessions: number;
      remainingSessions: number;
      expiresAt: string | null;
    } | null;
  }>({
    queryKey: ['/api/classes', id, 'my-pass'],
    queryFn: () =>
      authedJson<{
        pass: {
          id: string;
          totalSessions: number;
          usedSessions: number;
          remainingSessions: number;
          expiresAt: string | null;
        } | null;
      }>(`/api/classes/${id}/my-pass`),
    enabled: isAuthenticated && !!id,
  });

  // The pass sold here and every class it's good for (one pass can cover e.g.
  // all of a venue's yoga classes).
  const { data: passOfferData } = useQuery<{
    offer: {
      id: string;
      name: string;
      sessionCount: number;
      priceCents: number;
      isActive: boolean;
      classes: Array<{ id: string; title: string }>;
    } | null;
  }>({
    queryKey: ['/api/classes', id, 'pass-offer'],
    queryFn: () => fetchJson(`/api/classes/${id}/pass-offer`),
    enabled: !!id && (!!classData?.passEnabled || (myPassData?.pass?.remainingSessions ?? 0) > 0),
  });
  const passOffer = passOfferData?.offer ?? null;
  const otherPassClasses = passOffer?.classes.filter(c => c.id !== id) ?? [];

  const { data: creatorClasses = [] } = useQuery<CreatorClassSummaryDto[]>({
    queryKey: ['/api/classes', 'creator', classData?.creatorId],
    queryFn: async () => {
      if (!classData?.creatorId) return [];
      const creator = await fetchJson<CreatorClassesResponse>(
        `/api/creators/${classData.creatorId}`
      );
      return creator.classes || [];
    },
    enabled: !!classData?.creatorId,
  });

  const { data: classSessions = [] } = useQuery<ClassSessionDto[]>({
    queryKey: ['/api/classes', id, 'sessions'],
    queryFn: async (): Promise<ClassSessionDto[]> => {
      if (!id) return [];
      return fetchJson<ClassSessionDto[]>(`/api/classes/${id}/sessions`);
    },
    enabled: !!id,
    staleTime: 0, // Always refetch to get latest seat availability
    gcTime: 0, // Don't cache sessions data
  });

  useEffect(() => {
    if (classSessions.length > 0 && !selectedSessionId) {
      // Select the next upcoming session by default
      const now = new Date();
      const upcomingSessions = classSessions
        .filter(
          session => new Date(session.startDateTime.toString()) > now && !session.registrationClosed
        )
        .sort(
          (a, b) =>
            new Date(a.startDateTime.toString()).getTime() -
            new Date(b.startDateTime.toString()).getTime()
        );

      if (upcomingSessions.length > 0) {
        setSelectedSessionId(upcomingSessions[0].id);
      }
    }
  }, [classSessions, selectedSessionId]);

  const selectedSession = classSessions.find(session => session.id === selectedSessionId);
  // Promo can be windowed by session date, so price is per session.
  const selectedSessionStart = selectedSession?.startDateTime?.toString();
  const selectedPromoActive = classData
    ? isPromoActiveForSession(classData, selectedSessionStart)
    : false;
  const selectedEffectivePrice = classData
    ? effectiveUnitPrice(classData, selectedSessionStart)
    : 0;
  const isEnrolled = enrollmentData?.enrolled ?? false;
  const passRemaining = myPassData?.pass?.remainingSessions ?? 0;
  // Punch-pass bookings have no order; only those (not a paid checkout seat) can
  // add a guest after the fact. No cap on how many — each click adds one more.
  const isPunchPassBooking = isEnrolled && enrollmentData?.enrollment?.orderId == null;
  // Gated on the balance, not on the class still selling passes: a pass stays
  // usable after its product is taken off sale.
  const canAddGuestToExisting = isPunchPassBooking && passRemaining > 0;
  const availableSeats =
    selectedSession?.maxStudents == null ? 10 : (selectedSession?.availableSpots ?? 0);
  // Past the class's registration cutoff (closed wins over sold out in labels).
  const isSelectedSessionClosed = !!selectedSession?.registrationClosed;
  const isSelectedSessionSoldOut =
    !isSelectedSessionClosed && selectedSession?.maxStudents != null && availableSeats <= 0;
  const isSelectedSessionUnavailable = isSelectedSessionClosed || isSelectedSessionSoldOut;
  const unavailableLabel = isSelectedSessionClosed ? 'Registration closed' : 'Sold out';
  const maxQuantity = selectedSession?.maxStudents == null ? 10 : Math.min(availableSeats, 10); // Cap at 10 tickets per purchase
  // No upcoming session to book at all — none scheduled, or every future
  // session is full or past its registration cutoff. Drives the "join
  // waitlist" fallback.
  const hasBookableSession = classSessions.some(
    session =>
      new Date(session.startDateTime.toString()) > new Date() &&
      !session.registrationClosed &&
      (session.maxStudents == null || session.availableSpots > 0)
  );

  const enrollMutation = useMutation({
    mutationFn: async (): Promise<CheckoutLaunchPayload> => {
      if (!id || !selectedSessionId || !classData) {
        throw new Error('Please select a class session');
      }

      const response = await apiRequest('POST', '/api/checkout/sessions', {
        classId: id,
        sessionId: selectedSessionId,
        quantity,
      });
      const checkoutSession = (await response.json()) as CheckoutSessionCreateResponse;
      return {
        checkoutSessionId: checkoutSession.id,
        classId: id,
        sessionId: selectedSessionId,
        quantity,
      };
    },
    onSuccess: ({ checkoutSessionId, classId, sessionId, quantity: selectedQuantity }) => {
      void trackPublicFunnelEvent({
        eventName: 'detail_to_checkout',
        source: 'class_detail',
        destinationType: 'checkout',
        classId,
        sessionId,
        regionSlug: activeRegion?.slug,
        localitySlug: activeLocality?.slug,
        quantity: selectedQuantity,
        checkoutSessionId,
      });

      setLocation(`/checkout?checkout_session_id=${checkoutSessionId}&type=class`);
    },
    onError: (error: Error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }

      if (error.message?.includes('REGISTRATION_CLOSED')) {
        queryClient.invalidateQueries({ queryKey: ['/api/classes', id, 'sessions'] });
        toast({
          title: 'Registration closed',
          description: 'Registration for this session has closed. Please pick another date.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Error',
        description: 'Failed to start enrollment process. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Buy pass → go to the checkout cart, where the promo code is entered (kept
  // consistent with normal class checkout). The cart creates the PaymentIntent.
  const goToPassCheckout = () => {
    window.location.href = `/checkout?type=pass&class_id=${id}`;
  };

  const [bringGuestOpen, setBringGuestOpen] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  const redeemPassMutation = useMutation({
    mutationFn: async (input: { sessionId: string; guestName?: string; guestEmail?: string }) => {
      const res = await apiRequest(
        'POST',
        `/api/classes/${id}/sessions/${input.sessionId}/redeem-pass`,
        input.guestName ? { guestName: input.guestName, guestEmail: input.guestEmail } : undefined
      );
      return res.json();
    },
    onSuccess: (data: { remainingSessions?: number }, input) => {
      queryClient.invalidateQueries({ queryKey: ['/api/classes', id, 'my-pass'] });
      queryClient.invalidateQueries({ queryKey: ['/api/classes', id, 'sessions'] });
      setBringGuestOpen(false);
      setGuestName('');
      setGuestEmail('');
      const remaining = data?.remainingSessions ?? '';
      window.location.href = `/checkout/confirmation?pass=1&class_id=${id}&session_id=${input.sessionId}&remaining=${remaining}`;
    },
    onError: (err: Error) => {
      // Surface the server's specific reason (e.g. already booked, sold out).
      const msg = err.message?.includes('REGISTRATION_CLOSED')
        ? 'Registration for this session has closed. Please pick another date.'
        : err.message?.includes('Already enrolled')
          ? "You're already booked for this session."
          : err.message?.includes('Not enough punches')
            ? "You don't have enough punches left to bring a guest."
            : err.message?.includes('Not enough spots')
              ? 'Not enough spots left for another guest.'
              : err.message || 'Could not use a punch for this session.';
      toast({ title: 'Could not use a punch', description: msg, variant: 'destructive' });
    },
  });

  function submitBringGuest() {
    if (!selectedSessionId) return;
    if (!guestName.trim() || !guestEmail.trim()) {
      toast({
        title: 'Guest details needed',
        description: "Enter your guest's name and email so we can send them a waiver.",
        variant: 'destructive',
      });
      return;
    }
    redeemPassMutation.mutate({
      sessionId: selectedSessionId,
      guestName: guestName.trim(),
      guestEmail: guestEmail.trim(),
    });
  }

  const submitReviewMutation = useMutation({
    mutationFn: async ({ rating, body }: { rating: number; body: string }) => {
      const res = await apiRequest('POST', `/api/classes/${id}/reviews`, { rating, body });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Review submitted', description: 'Thank you for your feedback!' });
      setShowReviewForm(false);
      setReviewRating(0);
      setReviewBody('');
      queryClient.invalidateQueries({ queryKey: ['/api/classes', id, 'reviews'] });
    },
    onError: () => toast({ title: 'Failed to submit review', variant: 'destructive' }),
  });

  // Handle other errors but don't redirect on unauthorized (public page)
  useEffect(() => {
    if (error && !isUnauthorizedError(error as Error)) {
      toast({
        title: 'Error',
        description: 'Failed to load class details. Please try again.',
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  if (authLoading || isLoading) {
    return (
      <PublicShell routeKey="class-detail">
        <div className="page-shell flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </PublicShell>
    );
  }

  // Class details are publicly accessible

  if (!classData) {
    return (
      <PublicShell routeKey="not-found">
        <div className="page-shell flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Class Not Found</h1>
            <p className="text-muted-foreground mb-4">
              The class you're looking for doesn't exist.
            </p>
            <AutoRedirect />
            <Link href="/classes">
              <Button data-testid="button-back-to-classes">Back to Classes</Button>
            </Link>
          </div>
        </div>
      </PublicShell>
    );
  }

  const handleEnroll = () => {
    if (isDemoClassTitle(classData.title)) {
      return;
    }

    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }
    if (!selectedSessionId) {
      toast({
        title: 'Session Required',
        description: 'Please select a class session to enroll',
        variant: 'destructive',
      });
      return;
    }
    if (isSelectedSessionClosed) {
      toast({
        title: 'Registration Closed',
        description: 'Registration for this session has closed. Please pick another date.',
        variant: 'destructive',
      });
      return;
    }
    if (isSelectedSessionSoldOut) {
      toast({
        title: 'Session Sold Out',
        description: 'Please select another class session or check back later.',
        variant: 'destructive',
      });
      return;
    }
    if (selectedSession?.maxStudents != null && quantity > availableSeats) {
      toast({
        title: 'Not Enough Seats',
        description: `Only ${availableSeats} seats available for this session`,
        variant: 'destructive',
      });
      return;
    }
    enrollMutation.mutate();
  };

  const sessionTimezone =
    activeLocality?.timezone ?? activeRegion?.timezone ?? 'America/Indiana/Indianapolis';
  const isDemoClass = isDemoClassTitle(classData.title);
  const formatSessionDate = (start: string, end?: string) => {
    const endDate = end ? new Date(end) : new Date(new Date(start).getTime() + 60 * 60 * 1000);
    return formatSessionCompact(new Date(start), endDate, sessionTimezone);
  };

  return (
    <PublicShell
      routeKey="class-detail"
      stickyBar={
        isEnrolled ? (
          <div className="space-y-2">
            <Link href="/account/enrollments">
              <Button className="w-full rounded-full px-5">View my classes</Button>
            </Link>
            {canAddGuestToExisting && (
              <button
                type="button"
                className="w-full text-center text-xs font-medium text-primary underline underline-offset-2"
                onClick={() => setBringGuestOpen(true)}
              >
                Bring a guest with your pass
              </button>
            )}
          </div>
        ) : !hasBookableSession ? (
          <ClassWaitlistForm classId={id!} />
        ) : (
          <div className="space-y-2">
            <Button
              onClick={handleEnroll}
              disabled={
                isDemoClass ||
                enrollMutation.isPending ||
                !selectedSessionId ||
                isSelectedSessionUnavailable ||
                classSessions.length === 0
              }
              className="w-full rounded-full px-5"
            >
              {isDemoClass
                ? 'Demo Class'
                : enrollMutation.isPending
                  ? 'Processing...'
                  : !selectedSessionId
                    ? 'Select session to continue'
                    : isSelectedSessionUnavailable
                      ? unavailableLabel
                      : isAuthenticated
                        ? classData.pricingMode === 'free'
                          ? 'Enroll free'
                          : classData.pricingMode === 'pay_what_you_want'
                            ? 'Choose amount & enroll'
                            : // Use the session/window-aware effective price (same as the
                              // main booking rail) so an expired promo doesn't show "free".
                              selectedEffectivePrice <= 0
                              ? 'Enroll free'
                              : `Book now - $${(selectedEffectivePrice * quantity).toFixed(2)}`
                        : 'Login to enroll'}
            </Button>
            {/* Use a punch — mobile booking bar mirror of the booking card option. */}
            {isAuthenticated && passRemaining > 0 && (
              <Button
                variant="outline"
                className="w-full rounded-full px-5"
                disabled={
                  redeemPassMutation.isPending ||
                  !selectedSessionId ||
                  isSelectedSessionUnavailable ||
                  classSessions.length === 0
                }
                onClick={() =>
                  selectedSessionId && redeemPassMutation.mutate({ sessionId: selectedSessionId })
                }
              >
                {redeemPassMutation.isPending ? 'Booking…' : `Use a punch (${passRemaining} left)`}
              </Button>
            )}
            {isAuthenticated && passRemaining > 1 && (
              <button
                type="button"
                className="w-full text-center text-xs font-medium text-primary underline underline-offset-2"
                disabled={!selectedSessionId || isSelectedSessionUnavailable}
                onClick={() => setBringGuestOpen(true)}
              >
                Bring a guest with your pass
              </button>
            )}
            {isSelectedSessionSoldOut && (
              <p className="px-2 text-center text-xs font-medium text-destructive">
                Please select another class session or check back later.
              </p>
            )}
          </div>
        )
      }
    >
      {/* Hero Section */}
      <section className="bg-muted py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link href={returnHref}>
            <a className="mb-6 inline-flex text-sm font-medium text-muted-foreground hover:text-foreground">
              ← Back to results
            </a>
          </Link>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Class image */}
              <div className="aspect-video bg-muted rounded-2xl overflow-hidden mb-3 relative">
                <img
                  src={normalizeImageUrl(activeImage ?? classData.thumbnailUrl, PLACEHOLDER.thumb)}
                  alt={classData.title}
                  className="h-full w-full object-cover"
                  data-testid="class-detail-image"
                  onError={imageErrorFallback(PLACEHOLDER.thumb)}
                />
                {isEnrolled && (
                  <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Enrolled
                  </div>
                )}
              </div>

              {classData.galleryImages && classData.galleryImages.length > 0 && (
                <div
                  className="flex gap-2 overflow-x-auto overscroll-x-contain mb-6 pb-1"
                  data-testid="class-detail-gallery"
                >
                  {[classData.thumbnailUrl, ...classData.galleryImages]
                    .filter((url): url is string => Boolean(url))
                    .map((url, index) => (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        onClick={() => setActiveImage(url)}
                        className={`shrink-0 h-16 w-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          (activeImage ?? classData.thumbnailUrl) === url
                            ? 'border-primary'
                            : 'border-transparent'
                        }`}
                      >
                        <img
                          src={normalizeImageUrl(url, PLACEHOLDER.thumb)}
                          alt={`${classData.title} photo ${index + 1}`}
                          className="h-full w-full object-cover"
                          onError={imageErrorFallback(PLACEHOLDER.thumb)}
                        />
                      </button>
                    ))}
                </div>
              )}

              {/* Class Info */}
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    {classData.categoryId && (
                      <Badge className="bg-accent/10 text-accent" data-testid="class-category">
                        Category
                      </Badge>
                    )}
                    {classData.difficulty && (
                      <Badge variant="outline" data-testid="class-difficulty">
                        {classData.difficulty}
                      </Badge>
                    )}
                  </div>

                  <h1
                    className="text-3xl md:text-4xl font-bold text-foreground mb-4"
                    data-testid="class-title"
                  >
                    {classData.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
                    {classData.rating && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium" data-testid="class-rating">
                          {parseFloat(classData.rating).toFixed(1)}
                        </span>
                      </div>
                    )}
                    {classData.deliveryMode && isDeliveryMode(classData.deliveryMode) && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{DELIVERY_MODE_LABELS[classData.deliveryMode]}</span>
                      </div>
                    )}
                    {classData.venue?.name && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {activeRegion?.slug && activeLocality?.slug && classData.venue.slug ? (
                          <Link
                            href={`/regions/${activeRegion.slug}/${activeLocality.slug}/locations/${classData.venue.slug}`}
                            className="font-medium text-foreground hover:text-primary"
                          >
                            {classData.venue.name}
                          </Link>
                        ) : (
                          <span>{classData.venue.name}</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span data-testid="seat-availability">
                        {selectedSession
                          ? selectedSession.maxStudents == null
                            ? 'Open availability'
                            : isSelectedSessionUnavailable
                              ? unavailableLabel
                              : `${availableSeats} seats available`
                          : 'Select session for availability'}
                      </span>
                    </div>
                    {classData.duration && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-5 h-5" />
                        <span data-testid="class-duration">
                          {Math.floor(classData.duration / 60)}h {classData.duration % 60}m
                        </span>
                      </div>
                    )}
                  </div>

                  <p
                    className="whitespace-pre-wrap text-lg leading-relaxed text-foreground"
                    data-testid="class-description"
                  >
                    {classData.description}
                  </p>

                  {/* Location Information */}
                  {classData.location && (
                    <div className="mt-6 p-4 bg-muted rounded-lg">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-5 h-5 mt-0.5 text-muted-foreground" />
                        <div>
                          <h3
                            className="font-semibold text-foreground"
                            data-testid="class-location"
                          >
                            {classData.location}
                          </h3>
                          {classData.locationDetails && (
                            <p
                              className="text-sm text-muted-foreground mt-1"
                              data-testid="class-location-details"
                            >
                              {classData.locationDetails}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar — sticky on desktop */}
            <div className="space-y-5 lg:sticky lg:top-[calc(var(--shell-header-height)+1.5rem)] lg:h-fit">
              {/* Booking Card */}
              <Card className="rounded-[1.5rem] border-border/70 shadow-md">
                <CardContent className="space-y-5 p-6">
                  {/* Price + title */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{classData.title}</p>
                    <p
                      className="mt-1 text-4xl font-bold tracking-tight text-primary"
                      data-testid="class-price"
                    >
                      {classData.pricingMode === 'free' ? (
                        'Free'
                      ) : classData.pricingMode === 'pay_what_you_want' ? (
                        <>
                          Pay what you want
                          {classData.suggestedPrice && Number(classData.suggestedPrice) > 0 && (
                            <span className="ml-2 text-base font-normal text-muted-foreground">
                              suggested ${Number(classData.suggestedPrice).toFixed(0)}
                            </span>
                          )}
                        </>
                      ) : selectedPromoActive ? (
                        <>
                          <span className="mr-2 text-2xl font-normal text-muted-foreground line-through">
                            ${parseFloat(classData.price).toFixed(2)}
                          </span>
                          {selectedEffectivePrice <= 0
                            ? 'Free'
                            : `$${selectedEffectivePrice.toFixed(2)}`}
                          <span className="ml-1 text-base font-normal text-muted-foreground">
                            / person
                          </span>
                        </>
                      ) : (
                        <>
                          ${selectedEffectivePrice.toFixed(2)}
                          <span className="ml-1 text-base font-normal text-muted-foreground">
                            / person
                          </span>
                        </>
                      )}
                    </p>
                  </div>

                  <div className="h-px bg-border/60" />

                  {/* Session selector */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="session-select"
                      className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                    >
                      Session
                    </Label>
                    <Select value={selectedSessionId} onValueChange={setSelectedSessionId}>
                      <SelectTrigger data-testid="select-session" className="rounded-xl">
                        <SelectValue placeholder="Choose a date…" />
                      </SelectTrigger>
                      <SelectContent>
                        {classSessions.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No sessions available
                          </SelectItem>
                        ) : (
                          classSessions
                            .filter(
                              session => new Date(session.startDateTime.toString()) > new Date()
                            )
                            .map(session => {
                              const sessionPrice = effectiveUnitPrice(
                                classData,
                                session.startDateTime.toString()
                              );
                              // Only show per-session price when the promo window
                              // makes sessions differ; otherwise it's noise.
                              const priceLabel =
                                classData.pricingMode === 'standard' &&
                                classData.promoEnabled &&
                                (classData.promoStartsAt || classData.promoEndsAt)
                                  ? ` · ${sessionPrice <= 0 ? 'Free' : `$${sessionPrice.toFixed(2)}`}`
                                  : '';
                              return (
                                <SelectItem
                                  key={session.id}
                                  value={session.id}
                                  disabled={session.registrationClosed}
                                >
                                  {formatSessionDate(
                                    session.startDateTime.toString(),
                                    session.endDateTime.toString()
                                  )}{' '}
                                  ·{' '}
                                  {session.registrationClosed
                                    ? 'Registration closed'
                                    : session.maxStudents == null
                                      ? 'Open'
                                      : session.availableSpots <= 0
                                        ? 'Sold out'
                                        : `${session.availableSpots} left`}
                                  {priceLabel}
                                </SelectItem>
                              );
                            })
                        )}
                      </SelectContent>
                    </Select>

                    {selectedSession?.notes && (
                      <p className="text-xs text-muted-foreground">{selectedSession.notes}</p>
                    )}
                    {isSelectedSessionClosed && (
                      <p className="text-sm font-medium text-destructive">
                        Registration for this session has closed. Please pick another date.
                      </p>
                    )}
                    {isSelectedSessionSoldOut && (
                      <p className="text-sm font-medium text-destructive">
                        This session is sold out. Please select another class session or check back
                        later.
                      </p>
                    )}
                  </div>

                  {/* Quantity selector */}
                  {selectedSession && !isSelectedSessionUnavailable && (
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        Tickets
                      </Label>
                      <Select
                        value={quantity.toString()}
                        onValueChange={value => setQuantity(parseInt(value))}
                      >
                        <SelectTrigger data-testid="select-quantity" className="rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: maxQuantity }, (_, i) => i + 1).map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} {num === 1 ? 'ticket' : 'tickets'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Subtotal */}
                  {selectedSession && quantity > 1 && classData.pricingMode === 'standard' && (
                    <div className="flex items-center justify-between rounded-xl bg-muted/50 px-4 py-3 text-sm">
                      <span className="text-muted-foreground">
                        {quantity} × ${selectedEffectivePrice.toFixed(2)}
                      </span>
                      <span className="font-semibold">
                        ${(selectedEffectivePrice * quantity).toFixed(2)}
                      </span>
                    </div>
                  )}

                  {/* CTA */}
                  {isEnrolled ? (
                    <div className="space-y-2">
                      <Button
                        className="w-full rounded-full"
                        disabled
                        data-testid="button-enrolled"
                      >
                        Enrolled
                      </Button>
                      <Link href="/account/enrollments" className="block">
                        <Button
                          variant="outline"
                          className="w-full rounded-full"
                          data-testid="button-access-community"
                        >
                          View my classes
                        </Button>
                      </Link>
                      {canAddGuestToExisting && (
                        <button
                          type="button"
                          className="w-full text-center text-sm font-medium text-primary underline underline-offset-2"
                          onClick={() => setBringGuestOpen(true)}
                          data-testid="button-bring-guest-existing"
                        >
                          Bring a guest with your pass
                        </button>
                      )}
                    </div>
                  ) : !hasBookableSession ? (
                    <ClassWaitlistForm classId={id!} />
                  ) : (
                    <div className="space-y-2">
                      <Button
                        onClick={handleEnroll}
                        disabled={
                          isDemoClass ||
                          enrollMutation.isPending ||
                          !selectedSessionId ||
                          isSelectedSessionUnavailable ||
                          classSessions.length === 0
                        }
                        className="w-full rounded-full py-6 text-base font-semibold"
                        data-testid="button-enroll-now"
                      >
                        {isDemoClass
                          ? 'Demo Class'
                          : enrollMutation.isPending
                            ? 'Processing…'
                            : !selectedSessionId
                              ? 'Select a session to continue'
                              : isSelectedSessionUnavailable
                                ? unavailableLabel
                                : classSessions.length === 0
                                  ? 'No sessions available'
                                  : isAuthenticated
                                    ? classData.pricingMode === 'free'
                                      ? 'Enroll free'
                                      : classData.pricingMode === 'pay_what_you_want'
                                        ? 'Choose amount & enroll'
                                        : selectedEffectivePrice <= 0
                                          ? 'Enroll free'
                                          : `Book now — $${(selectedEffectivePrice * quantity).toFixed(2)}`
                                    : 'Sign in to book'}
                      </Button>

                      {/* Use a punch — same booking box as single-class checkout,
                          shown when the buyer has an active pass with punches left. */}
                      {isAuthenticated && passRemaining > 0 && (
                        <Button
                          variant="outline"
                          className="w-full rounded-full py-6 text-base font-semibold"
                          disabled={
                            redeemPassMutation.isPending ||
                            !selectedSessionId ||
                            isSelectedSessionUnavailable ||
                            classSessions.length === 0
                          }
                          onClick={() =>
                            selectedSessionId &&
                            redeemPassMutation.mutate({ sessionId: selectedSessionId })
                          }
                          data-testid="button-use-punch"
                        >
                          {redeemPassMutation.isPending
                            ? 'Booking…'
                            : !selectedSessionId
                              ? 'Select a session to use a punch'
                              : isSelectedSessionUnavailable
                                ? unavailableLabel
                                : `Use a punch (${passRemaining} left)`}
                        </Button>
                      )}
                      {isAuthenticated && passRemaining > 1 && (
                        <button
                          type="button"
                          className="w-full text-center text-sm font-medium text-primary underline underline-offset-2"
                          disabled={!selectedSessionId || isSelectedSessionUnavailable}
                          onClick={() => setBringGuestOpen(true)}
                          data-testid="button-bring-guest"
                        >
                          Bring a guest with your pass
                        </button>
                      )}
                    </div>
                  )}

                  {/* Cancellation policy */}
                  <div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm">
                    <p className="font-medium text-foreground">Cancellation Policy</p>
                    <p className="mt-1 text-muted-foreground">
                      {getCancellationPolicyStatement(
                        classData.cancellationPolicy as CancellationPolicy
                      )}
                    </p>
                    {getRegistrationCutoffStatement(classData) && (
                      <p className="mt-2 text-muted-foreground">
                        {getRegistrationCutoffStatement(classData)}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Punch Pass — directly under the booking card so it's visible. Also
                  shown to a holder after the pass goes off sale, so they can still
                  see their balance. */}
              {isAuthenticated && (classData?.passEnabled || myPassData?.pass) && (
                <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                  <CardContent className="space-y-4 p-6">
                    <h2 className="text-lg font-semibold text-foreground">
                      {otherPassClasses.length > 0 && passOffer ? passOffer.name : 'Punch Pass'}
                    </h2>

                    {/* A shared pass: name every other class a punch can be used on. */}
                    {otherPassClasses.length > 0 && (
                      <div className="text-sm text-muted-foreground" data-testid="pass-good-for">
                        <p>Also good for:</p>
                        <ul className="mt-1 space-y-0.5">
                          {otherPassClasses.map(c => (
                            <li key={c.id}>
                              <Link
                                href={`/classes/${c.id}`}
                                className="font-medium text-primary underline-offset-2 hover:underline"
                              >
                                {c.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Current balance (when the buyer already holds a pass). Punches
                        are redeemed from the "Use a punch" button in the booking card. */}
                    {myPassData?.pass && (
                      <div className="rounded-xl border border-border bg-muted/40 p-4">
                        <p className="font-medium">
                          {myPassData.pass.remainingSessions} of {myPassData.pass.totalSessions}{' '}
                          sessions remaining
                        </p>
                        {myPassData.pass.expiresAt && (
                          <p className="mt-0.5 text-sm text-muted-foreground">
                            Expires {new Date(myPassData.pass.expiresAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Buy a pass — always available while on sale, so a buyer can
                        stack additional passes even while they hold one. */}
                    {classData.passEnabled && (
                      <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
                        <div>
                          <p className="font-medium">
                            {classData.passSessionCount}-session pass — $
                            {((classData.passPrice ?? 0) / 100).toFixed(2)}
                          </p>
                          <p className="mt-0.5 text-sm text-muted-foreground">
                            Valid for 1 year after purchase
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={goToPassCheckout}
                          data-testid="button-buy-pass"
                        >
                          {myPassData?.pass ? 'Buy another pass' : 'Buy pass'}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Companion online livestream class */}
              {classData.livestreamClass && (
                <Card className="rounded-[1.5rem] border-primary/30 bg-primary/5">
                  <CardContent className="space-y-3 p-5">
                    <div className="flex items-center gap-2">
                      <MonitorPlay className="h-4 w-4 shrink-0 text-primary" />
                      <p className="text-sm font-semibold text-foreground">
                        Also streamed live online
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Sold out, or can&apos;t make it into the studio? Join this class as a live
                      online stream from anywhere.
                    </p>
                    <Link
                      href={`/classes/${classData.livestreamClass.id}`}
                      className="block"
                      data-testid="link-livestream-class"
                    >
                      <div className="flex items-center gap-3 rounded-xl border border-border/70 bg-background p-3 transition-colors hover:border-primary/50">
                        {classData.livestreamClass.thumbnailUrl && (
                          <img
                            src={classData.livestreamClass.thumbnailUrl}
                            alt={classData.livestreamClass.title}
                            className="h-12 w-12 shrink-0 rounded-lg object-cover"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">
                            {classData.livestreamClass.title}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {classData.livestreamClass.pricingMode === 'free' ||
                            (classData.livestreamClass.promoEnabled &&
                              classData.livestreamClass.promoPrice != null &&
                              Number(classData.livestreamClass.promoPrice) <= 0)
                              ? 'Free'
                              : classData.livestreamClass.promoEnabled &&
                                  classData.livestreamClass.promoPrice != null
                                ? `$${parseFloat(classData.livestreamClass.promoPrice).toFixed(2)} / person`
                                : `$${parseFloat(classData.livestreamClass.price).toFixed(2)} / person`}
                          </p>
                        </div>
                        <span className="shrink-0 text-sm font-medium text-primary">View →</span>
                      </div>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {/* Creator Info */}
              {classData.creator && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Meet Your Instructor</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="w-16 h-16">
                        <AvatarImage
                          src={
                            classData.creator.profileImageUrl
                              ? classData.creator.profileImageUrl.startsWith('http')
                                ? classData.creator.profileImageUrl
                                : `${window.location.origin}${classData.creator.profileImageUrl}`
                              : undefined
                          }
                          alt={classData.creator.displayName}
                          data-testid="creator-avatar"
                        />
                        <AvatarFallback>
                          {classData.creator.displayName
                            .split(' ')
                            .map((n: string) => n[0])
                            .join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <Link href={`/creators/${classData.creator.id}`}>
                          <h3
                            className="font-semibold text-foreground hover:text-primary cursor-pointer transition-colors"
                            data-testid="creator-name"
                          >
                            {classData.creator.displayName}
                          </h3>
                        </Link>
                        <p
                          className="text-sm text-accent font-medium"
                          data-testid="creator-specialty"
                        >
                          {classData.creator.creatorHeadline}
                        </p>
                        {classData.creator.bio && (
                          <p
                            className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-muted-foreground"
                            data-testid="creator-bio"
                          >
                            {classData.creator.bio}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Link href={`/creators/${classData.creator.id}`} className="flex-1">
                        <Button
                          variant="outline"
                          className="w-full text-sm"
                          data-testid="button-view-profile"
                        >
                          View Profile
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="icon"
                        className="text-accent hover:bg-accent/10"
                        data-testid="button-tip-creator"
                      >
                        <Coffee className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      {reviewsData && (reviewsData.count > 0 || isEnrolled) && (
        <section className="py-12 border-t border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Reviews</h2>
                {reviewsData.count > 0 && reviewsData.averageRating && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star
                          key={n}
                          className={`w-4 h-4 ${
                            n <= Math.round(reviewsData.averageRating!)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-muted-foreground'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {reviewsData.averageRating.toFixed(1)} · {reviewsData.count}{' '}
                      {reviewsData.count === 1 ? 'review' : 'reviews'}
                    </span>
                  </div>
                )}
              </div>
              {isEnrolled && (
                <Button variant="outline" size="sm" onClick={() => setShowReviewForm(v => !v)}>
                  {showReviewForm ? 'Cancel' : 'Write a review'}
                </Button>
              )}
            </div>

            {showReviewForm && (
              <Card className="mb-6">
                <CardContent className="p-4 space-y-3">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setReviewRating(n)}
                        aria-label={`Rate ${n} stars`}
                      >
                        <Star
                          className={`w-6 h-6 ${
                            n <= reviewRating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-muted-foreground'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <Textarea
                    placeholder="Share your experience (optional)…"
                    value={reviewBody}
                    onChange={e => setReviewBody(e.target.value)}
                    rows={3}
                  />
                  <Button
                    size="sm"
                    disabled={reviewRating === 0 || submitReviewMutation.isPending}
                    onClick={() =>
                      submitReviewMutation.mutate({ rating: reviewRating, body: reviewBody })
                    }
                  >
                    {submitReviewMutation.isPending ? 'Submitting…' : 'Submit review'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {reviewsData.reviews.length > 0 && (
              <div className="space-y-4">
                {reviewsData.reviews.map(review => (
                  <Card key={review.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {review.user.firstName} {review.user.lastName}
                          </span>
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star
                                key={n}
                                className={`w-3.5 h-3.5 ${
                                  n <= review.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-muted-foreground'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {review.body && (
                        <p className="text-sm text-muted-foreground">{review.body}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Related Classes */}
      {creatorClasses.length > 1 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-foreground mb-8">
              More from {classData.creator?.displayName}
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {creatorClasses
                .filter(cls => cls.id !== id)
                .slice(0, 3)
                .map(cls => (
                  <Card key={cls.id} className="card-hover">
                    <Link href={`/classes/${cls.id}`}>
                      <img
                        src={normalizeImageUrl(cls.thumbnailUrl, PLACEHOLDER.thumb)}
                        alt={cls.title}
                        className="w-full h-40 object-cover rounded-t-lg cursor-pointer"
                        data-testid={`related-class-image-${cls.id}`}
                        onError={imageErrorFallback(PLACEHOLDER.thumb, 'Class Thumbnail')}
                      />
                    </Link>

                    <CardContent className="p-4">
                      <Link href={`/classes/${cls.id}`}>
                        <h3
                          className="font-semibold text-foreground hover:text-primary cursor-pointer transition-colors mb-2"
                          data-testid={`related-class-title-${cls.id}`}
                        >
                          {cls.title}
                        </h3>
                      </Link>
                      <div className="flex justify-between items-center">
                        <span
                          className="text-lg font-bold text-primary"
                          data-testid={`related-class-price-${cls.id}`}
                        >
                          ${cls.price}
                        </span>
                        {cls.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm text-muted-foreground">
                              {parseFloat(cls.rating).toFixed(1)}
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>
        </section>
      )}

      <Dialog open={bringGuestOpen} onOpenChange={setBringGuestOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bring a guest</DialogTitle>
            <DialogDescription>
              {isEnrolled
                ? "Uses 1 additional punch from your pass for your guest's seat — you've already used yours for this session."
                : 'Uses 2 punches from your pass — one for you, one for your guest.'}{' '}
              We'll email them a waiver right away if this class requires one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="guest-name">Guest name</Label>
              <Input
                id="guest-name"
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                placeholder="Guest's full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="guest-email">Guest email</Label>
              <Input
                id="guest-email"
                type="email"
                value={guestEmail}
                onChange={e => setGuestEmail(e.target.value)}
                placeholder="guest@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBringGuestOpen(false)}
              disabled={redeemPassMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={submitBringGuest}
              disabled={redeemPassMutation.isPending}
              data-testid="button-confirm-bring-guest"
            >
              {redeemPassMutation.isPending
                ? 'Booking…'
                : isEnrolled
                  ? 'Add guest (1 punch)'
                  : 'Use 2 punches'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PublicShell>
  );
}
